/**
 * Widget Layout Component
 *
 * Wrapper component for widget routes that:
 * - Loads provider configuration and applies theming
 * - Wraps all widget routes with WidgetAuthProvider
 * - Provides consistent header navigation
 * - Renders nested routes via Outlet
 */

import { Outlet, useLocation } from 'react-router-dom';
import { ProviderConfigProvider, useProviderConfig } from '../../contexts/ProviderConfigContext';
import { WidgetAuthProvider } from '../../contexts/WidgetAuthContext';
import { WidgetHeader } from './WidgetHeader';

/**
 * Widget Theme Wrapper
 * Applies provider-specific theme (CSS variables) from config
 */
function WidgetThemeWrapper({ children }: { children: React.ReactNode }) {
  const { config, isLoading, error } = useProviderConfig();

  // Apply CSS variables for theming
  if (config) {
    const root = document.documentElement;
    if (config.primaryColor) {
      root.style.setProperty('--primary', config.primaryColor);
    }
  }

  // Show loading state
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading provider configuration...</p>
        </div>
      </div>
    );
  }

  // Show error state
  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-red-600 mb-2">Configuration Error</h2>
          <p className="text-gray-600">{error}</p>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}

/**
 * Widget Content Wrapper
 * Wraps content with consistent layout structure
 */
function WidgetContent() {
  const location = useLocation();

  // Routes that should NOT show the header (catalog has its own header, login/register are standalone)
  const hideHeaderRoutes = ['/login', '/register'];
  const shouldShowHeader = !hideHeaderRoutes.some(route =>
    location.pathname.includes(route)
  ) && !location.pathname.match(/\/widget\/[^/]+\/?$/); // Hide on catalog root

  // Routes that should have full-width content (no container)
  const fullWidthRoutes = ['', '/order', '/checkout', '/confirmation'];
  const isFullWidth = fullWidthRoutes.some(route =>
    location.pathname.endsWith(route) || location.pathname.includes(route)
  );

  return (
    <div className="min-h-screen bg-gray-50">
      {shouldShowHeader && <WidgetHeader />}
      <main className={isFullWidth ? '' : 'max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8'}>
        <Outlet />
      </main>
    </div>
  );
}

/**
 * Widget Layout
 * Main layout wrapper for all widget routes
 */
export function WidgetLayout() {
  return (
    <ProviderConfigProvider>
      <WidgetThemeWrapper>
        <WidgetAuthProvider>
          <WidgetContent />
        </WidgetAuthProvider>
      </WidgetThemeWrapper>
    </ProviderConfigProvider>
  );
}
